/** The note on a coverage window that a document is waiting beside (ADR-0166).
 *
 * **A note, not a state.** The cell keeps its own census state, its colour and its word; this says
 * only that a document *may* cover the window and is *waiting for a person to review it*, links to
 * it, and says plainly that it does not count until a person verifies it. The words are fixed by
 * the ADR: never "received", "in", "done" or a check mark, because each of those would read as the
 * window being accounted for, and an unreviewed document is not data (`AWAITING_REVIEW` is the only
 * route into `VERIFIED`).
 *
 * It renders nothing for a state that may not carry one (`mayCarryNote`), and nothing for an empty
 * list: a note that appears with no document behind it is a claim with nothing under it.
 *
 * **On a `missing` window it also says what the waiting does to the reminder** (ADR-0206): the
 * data-gap reminder is held for `holdDays` after a document arrives and sent if it is still waiting
 * then. `holdDays` is the server's setting (`gap_reminder_hold_days`), never a number typed here, and
 * the line is about the reminder only: the window keeps its state either way.
 */

import { Hourglass } from "@phosphor-icons/react";
import { Link } from "@tanstack/react-router";

import type { WaitingDocument } from "../../api/coverageRollup";
import { kindLabel } from "../../lib/documentKinds";
import { stageWord } from "../../lib/documentStages";
import { shortDate } from "../../lib/format";
import { keepSearch } from "../search";
import { STATE } from "../sourceModel";
import styles from "./WaitingNote.module.css";
import { mayCarryNote, unitWord } from "./waiting";

export function WaitingNote({
  documents,
  state,
  cadence,
  holdDays,
}: {
  documents: readonly WaitingDocument[];
  /** The window's census state, which this note never changes. */
  state: string;
  cadence: string;
  /** Days the gap reminder is held after a document arrives (`GET /coverage`). Absent: no line. */
  holdDays?: number;
}) {
  if (documents.length === 0 || !mayCarryNote(state)) return null;
  const several = documents.length > 1;
  return (
    <div className={styles.note} data-testid="cell-waiting" data-count={documents.length}>
      <p className={styles.head}>
        <Hourglass size={14} weight="bold" aria-hidden="true" className={styles.glyph} />
        <span>
          {several ? `${documents.length} documents that may cover this ${unitWord(cadence)} are` : `A document that may cover this ${unitWord(cadence)} is`}{" "}
          waiting for review
        </span>
      </p>
      <ul className={styles.list} aria-label="Documents waiting for review">
        {documents.map((document) => (
          <li key={document.document_id}>
            <Link
              className={styles.link}
              to="/evidence/$documentId"
              params={{ documentId: document.document_id }}
              search={keepSearch}
              data-testid="cell-waiting-link"
            >
              {kindLabel(document.kind)}, {shortDate(document.period_start)} &ndash;{" "}
              {shortDate(document.period_end)}
            </Link>
            <span className={styles.meta}>{stageWord(document.stage)}</span>
          </li>
        ))}
      </ul>
      <p className={styles.honest} data-testid="cell-waiting-honest">
        {several ? "They do" : "It does"} not count until a person verifies {several ? "them" : "it"}. This window
        stays {STATE[state]?.short ?? state.replace(/_/g, " ")} until then.
      </p>
      {state === "missing" && holdDays !== undefined ? (
        <p className={styles.honest} data-testid="cell-waiting-reminder" data-hold-days={holdDays}>
          The reminder about this gap is held while {several ? "they wait" : "it waits"}, for up to {holdDays}{" "}
          {holdDays === 1 ? "day" : "days"} after the first arrived. If {several ? "one is" : "it is"} still waiting
          then, the reminder is sent.
        </p>
      ) : null}
    </div>
  );
}
